<template>
  <div style="min-height: var(--app-viewport-height, 100vh); background: linear-gradient(180deg, #EDF3FD 0%, #F8FAFC 100%);">
    <Loader :loading="showLoader" />

    <v-container fluid class="py-4 py-md-6">
      <v-row justify="center">
        <v-col cols="12" lg="11" xl="10">
          <!-- Header Toolbar -->
          <v-sheet color="transparent">
            <v-card
              rounded="xl"
              elevation="4"
              class="px-4 px-md-6 py-4"
              style="background: linear-gradient(135deg, #285096 0%, #23A5E6 50%, #59C4FF 100%);"
            >
              <div class="d-flex align-center">
                <div>
                  <div class="text-h6 font-weight-semibold" style="color: #ffffff;">
                    {{ $t('incidents.title-list') }}
                  </div>
                  <div class="text-body-2" style="color: rgba(255,255,255,0.85);">
                    {{ userName }}
                  </div>
                </div>

                <v-spacer />

                <v-btn
                  variant="flat"
                  color="white"
                  rounded="lg"
                  class="text-none mr-2"
                  @click="openCreateDialog"
                >
                  <v-icon start size="20" color="#285096">mdi-plus-circle</v-icon>
                  <span style="color: #285096;" class="font-weight-semibold">
                    {{ $t('incidents.create') }}
                  </span>
                </v-btn>

                <v-btn
                  icon
                  variant="text"
                  color="white"
                  :loading="isRefreshing"
                  @click="handleRefresh"
                >
                  <v-icon size="24">mdi-refresh</v-icon>
                </v-btn>
              </div>
            </v-card>
          </v-sheet>

          <!-- Main Content -->
          <v-sheet color="transparent" class="mt-4">
            <v-card color="white" elevation="2" rounded="xl" class="pa-4 pa-md-6">
              <!-- Status Summary Chips -->
              <v-row class="mb-3" align="center" no-gutters>
                <v-col cols="12" class="d-flex flex-wrap ga-2">
                  <v-chip
                    variant="tonal"
                    color="#DC3545"
                    size="small"
                    label
                    prepend-icon="mdi-alert-circle"
                  >
                    {{ $t('incidents.incident-open') }} • {{ statusSummary.open }}
                  </v-chip>

                  <v-chip
                    variant="tonal"
                    color="#28A745"
                    size="small"
                    label
                    prepend-icon="mdi-check-circle"
                  >
                    {{ $t('office.done') }} • {{ statusSummary.done }}
                  </v-chip>

                  <v-spacer />

                  <span
                    v-if="filteredIncidents.length"
                    class="text-caption text-md-body-2"
                    style="color: #718096;"
                  >
                    Showing {{ filteredIncidents.length }} of
                    {{ incidents.length }} {{ $t('incidents.title').toLowerCase() }}
                  </span>
                </v-col>
              </v-row>

              <!-- Search + Filter Controls -->
              <v-row align="center" class="mb-4" no-gutters>
                <v-col cols="12" sm="6" md="5" class="mb-3 mb-sm-0 pr-sm-3">
                  <v-text-field
                    v-model="searchQuery"
                    :placeholder="$t('office.search') + '...'"
                    prepend-inner-icon="mdi-magnify"
                    variant="solo-filled"
                    density="comfortable"
                    hide-details
                    clearable
                    rounded="lg"
                    base-color="#9AA6B1"
                    color="#23A5E6"
                  />
                </v-col>

                <v-spacer />

                <v-col cols="auto" class="d-flex flex-wrap ga-2">
                  <v-btn
                    :variant="showFilters ? 'flat' : 'outlined'"
                    :color="showFilters ? '#285096' : '#4E5965'"
                    rounded="lg"
                    size="default"
                    class="text-none"
                    @click="showFilters = !showFilters"
                  >
                    <v-icon start size="20">mdi-filter-variant</v-icon>
                    <span class="d-none d-sm-inline">{{ $t('navigation-bar.filter') }}</span>
                    <v-chip
                      v-if="activeFilterCount > 0"
                      size="x-small"
                      color="#23A5E6"
                      class="ml-2"
                      label
                    >
                      {{ activeFilterCount }}
                    </v-chip>
                  </v-btn>

                  <v-menu location="bottom end">
                    <template #activator="{ props }">
                      <v-btn
                        v-bind="props"
                        variant="outlined"
                        color="#4E5965"
                        rounded="lg"
                        size="default"
                        class="text-none"
                      >
                        <v-icon start size="20">mdi-sort</v-icon>
                        <span class="d-none d-sm-inline">{{ currentSortLabel }}</span>
                        <v-icon end size="18">mdi-chevron-down</v-icon>
                      </v-btn>
                    </template>
                    <v-list density="compact" rounded="lg" elevation="3" min-width="220">
                      <v-list-subheader class="text-caption">
                        {{ $t('navigation-bar.sort') }}
                      </v-list-subheader>
                      <v-list-item
                        v-for="option in sortOptions"
                        :key="option.value"
                        :active="currentSort === option.value"
                        active-color="#285096"
                        rounded="lg"
                        @click="currentSort = option.value"
                      >
                        <v-list-item-title>{{ option.label }}</v-list-item-title>
                        <template #append>
                          <v-icon v-if="currentSort === option.value" size="18" color="#23A5E6">
                            mdi-check
                          </v-icon>
                        </template>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </v-col>
              </v-row>

              <!-- Quick Status Filter -->
              <v-row class="mb-4" no-gutters>
                <v-col cols="12" class="d-flex flex-wrap ga-2">
                  <span
                    class="text-caption text-uppercase"
                    style="color: #A0AEC0; letter-spacing: 0.08em;"
                  >
                    {{ $t('incidents.incident-status') }}
                  </span>
                  <v-chip-group
                    v-model="filters.status"
                    selected-class=""
                    class="ml-4"
                    column
                  >
                    <v-chip value="all" label size="small" variant="outlined" color="#4E5965" class="text-none">
                      {{ $t('booking.all') }}
                    </v-chip>
                    <v-chip value="open" label size="small" variant="tonal" color="#DC3545" class="text-none">
                      {{ $t('incidents.incident-open') }}
                    </v-chip>
                    <v-chip value="done" label size="small" variant="tonal" color="#28A745" class="text-none">
                      {{ $t('office.done') }}
                    </v-chip>
                  </v-chip-group>
                </v-col>
              </v-row>

              <!-- Expanded Filters -->
              <v-expand-transition>
                <v-card
                  v-if="showFilters"
                  rounded="xl"
                  class="mb-4 pa-4 pa-md-5"
                  elevation="0"
                  style="background: linear-gradient(135deg, #F3F6FF 0%, #FFFFFF 100%); border: 1px solid #E2E8F0;"
                >
                  <v-row dense>
                    <v-col cols="12" sm="6" md="3">
                      <v-select
                        v-model="filters.timeRange"
                        :items="timeRangeOptions"
                        item-title="label"
                        item-value="value"
                        :label="$t('occupancy.time-range')"
                        variant="solo-filled"
                        density="comfortable"
                        hide-details
                        rounded="lg"
                        prepend-inner-icon="mdi-calendar-range"
                        color="#23A5E6"
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <v-select
                        v-model="filters.category"
                        :items="categoryOptions"
                        item-title="label"
                        item-value="value"
                        :label="$t('incidents.incident-category')"
                        variant="solo-filled"
                        density="comfortable"
                        hide-details
                        rounded="lg"
                        prepend-inner-icon="mdi-tag-outline"
                        color="#23A5E6"
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <v-select
                        v-model="filters.floor"
                        :items="floorOptions"
                        item-title="label"
                        item-value="value"
                        :label="$t('office.floor')"
                        variant="solo-filled"
                        density="comfortable"
                        hide-details
                        rounded="lg"
                        prepend-inner-icon="mdi-stairs"
                        color="#23A5E6"
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <v-btn
                        v-if="hasActiveFilters"
                        variant="tonal"
                        color="error"
                        rounded="lg"
                        class="text-none w-100"
                        style="height: 48px;"
                        @click="clearFilters"
                      >
                        <v-icon start size="18">mdi-close-circle</v-icon>
                        {{ $t('users.reset') }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card>
              </v-expand-transition>

              <!-- Loading State -->
              <div v-if="isLoading" class="d-flex flex-column align-center justify-center py-16">
                <v-progress-circular indeterminate color="#23A5E6" size="56" width="4" />
                <p class="text-body-1 mt-4" style="color: #4E5965;">
                  {{ $t('incidents.loading') }}
                </p>
              </div>

              <!-- Empty State -->
              <v-card
                v-else-if="filteredIncidents.length === 0"
                variant="outlined"
                rounded="xl"
                class="pa-12 text-center"
              >
                <v-icon size="96" color="#B7BFC7">mdi-clipboard-text-off-outline</v-icon>
                <h3 class="text-h6 mt-4 font-weight-semibold" style="color: #373F47;">
                  {{ $t('incidents.incident-not-found') }}
                </h3>
                <p class="text-body-2 mt-2" style="color: #9AA6B1;">
                  {{ hasActiveFilters ? $t('booking.apply-filter') : $t('incidents.incident-not-found') }}
                </p>
              </v-card>

              <!-- Desktop Table -->
              <v-card v-else variant="flat" rounded="xl" class="d-none d-md-block">
                <v-data-table
                  :headers="tableHeaders"
                  :items="filteredIncidents"
                  :items-per-page="10"
                  hover
                  density="comfortable"
                  class="responsive-table"
                  @click:row="(e, { item }) => openIncidentDialog(item)"
                >
                  <template #[`item.incidentNumber`]="{ item }">
                    <span class="font-weight-medium text-no-wrap">#{{ item.serviceNumber || item.incidentNumber || '-' }}</span>
                  </template>

                  <template #[`item.serviceCategoryName`]="{ item }">
                    <div class="d-flex align-center">
                      <v-avatar color="#EDF3FD" size="28" class="mr-2 flex-shrink-0">
                        <v-icon size="14" color="#285096">{{ getCategoryIcon(item.serviceCategoryName || item.incidentCategoryName) }}</v-icon>
                      </v-avatar>
                      <span class="font-weight-medium" style="color:#001437; word-break: break-word; line-height: 1.3;">
                        {{ item.serviceCategoryName || item.incidentCategoryName || $t('occupancy.all-categories') }}
                      </span>
                    </div>
                  </template>

                  <template #[`item.description`]="{ value }">
                    <span class="d-inline-block" style="word-break: break-word; line-height: 1.3;">{{ value }}</span>
                  </template>

                  <template #[`item.location`]="{ item }">
                    <div class="d-flex align-center ga-1">
                      <v-icon size="14" color="#9AA6B1" class="flex-shrink-0">mdi-map-marker</v-icon>
                      <span style="word-break: break-word; line-height: 1.3;">{{ getLocationDisplay(item) }}</span>
                    </div>
                  </template>

                  <template #[`item.floorName`]="{ item }">
                    <span class="text-no-wrap">{{ getFloorDisplay(item) }}</span>
                  </template>

                  <template #[`item.creationTime`]="{ value }">
                    <span class="text-no-wrap" style="font-size: 0.875rem;">{{ formatDateTime(value) }}</span>
                  </template>

                  <template #[`item.state`]="{ value }">
                    <div class="d-flex justify-end">
                      <v-chip
                        :color="getStatusColor(value)"
                        size="small"
                        variant="tonal"
                        label
                        class="text-capitalize font-weight-bold"
                      >
                        {{ getStatusLabel(value) }}
                      </v-chip>
                    </div>
                  </template>
                </v-data-table>
              </v-card>

              <!-- Mobile Cards -->
              <div class="d-md-none">
                <v-row dense>
                  <v-col v-for="incident in filteredIncidents" :key="incident.id" cols="12">
                    <v-card
                      rounded="xl"
                      elevation="2"
                      class="mb-3 incident-card"
                      @click="openIncidentDialog(incident)"
                    >
                      <!-- Header with Status Badge -->
                      <div class="pa-4 pb-2 d-flex align-start justify-space-between">
                        <div class="flex-grow-1 pr-3">
                          <div class="d-flex align-center mb-1">
                            <v-chip
                              :color="getStatusColor(incident.state)"
                              size="x-small"
                              variant="flat"
                              class="text-uppercase font-weight-bold mr-2"
                              style="letter-spacing: 0.5px;"
                            >
                              {{ getStatusLabel(incident.state) }}
                            </v-chip>
                            <span class="text-caption" style="color: #9AA6B1;">
                              #{{ incident.serviceNumber || incident.incidentNumber || '-' }}
                            </span>
                          </div>
                          <h3 class="text-body-1 font-weight-semibold mb-1" style="color: #001437; line-height: 1.4;">
                            {{ incident.description }}
                          </h3>
                          <div class="d-flex align-center text-caption" style="color: #9AA6B1;">
                            <v-icon size="12" color="#9AA6B1" class="mr-1">mdi-clock-outline</v-icon>
                            {{ formatDateTime(incident.creationTime) }}
                          </div>
                        </div>
                        
                        <v-avatar 
                          :color="getStatusColor(incident.state)" 
                          size="40" 
                          variant="tonal"
                          class="flex-shrink-0"
                        >
                          <v-icon size="20" :color="getStatusColor(incident.state)">
                            {{ getStatusIcon(incident.state) }}
                          </v-icon>
                        </v-avatar>
                      </div>

                      <v-divider class="mx-4" style="opacity: 0.5;" />

                      <!-- Details Section -->
                      <div class="pa-4 pt-3">
                        <v-row dense class="mb-2">
                          <!-- Category -->
                          <v-col cols="12" class="mb-2">
                            <div class="detail-item">
                              <v-icon size="16" color="#285096" class="mr-2">
                                {{ getCategoryIcon(incident.serviceCategoryName || incident.incidentCategoryName) }}
                              </v-icon>
                              <span class="text-body-2 font-weight-medium" style="color: #285096;">
                                {{ incident.serviceCategoryName || incident.incidentCategoryName || $t('occupancy.all-categories') }}
                              </span>
                            </div>
                          </v-col>

                          <!-- Location -->
                          <v-col cols="12" sm="6" class="mb-2">
                            <div class="detail-item">
                              <v-icon size="14" color="#718096" class="mr-1">mdi-map-marker</v-icon>
                              <span class="text-body-2" style="color: #4E5965;">
                                {{ getLocationDisplay(incident) }}
                              </span>
                            </div>
                          </v-col>

                          <!-- Floor -->
                          <v-col cols="12" sm="6" class="mb-2">
                            <div class="detail-item">
                              <v-icon size="14" color="#718096" class="mr-1">mdi-stairs</v-icon>
                              <span class="text-body-2" style="color: #4E5965;">
                                {{ $t('office.floor') }}: {{ getFloorDisplay(incident) }}
                              </span>
                            </div>
                          </v-col>
                        </v-row>

                        <!-- Action Button -->
                        <v-btn
                          variant="tonal"
                          color="#285096"
                          size="small"
                          rounded="lg"
                          class="text-none mt-2 w-100"
                          prepend-icon="mdi-eye"
                        >
                          {{ $t('navigation-bar.view') }} {{ $t('incidents.title').slice(0, -1) }}
                        </v-btn>
                      </div>
                    </v-card>
                  </v-col>
                </v-row>
              </div>
            </v-card>
          </v-sheet>
        </v-col>
      </v-row>
    </v-container>

    <!-- Incident Dialog -->
    <v-dialog v-model="incidentDialog" max-width="650" scrollable persistent>
      <v-card rounded="xl">
        <v-card-title
          class="pa-4 d-flex align-center"
          style="background: linear-gradient(135deg, #285096 0%, #23A5E6 100%);"
        >
          <span class="text-h6 font-weight-semibold" style="color: white;">
            {{ isEditMode ? $t('incidents.incident-details') : $t('incidents.create-incident') }}
          </span>
          <v-spacer />
          <v-btn icon variant="text" density="comfortable" @click="closeDialog">
            <v-icon color="white">mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider />

        <v-card-text class="pa-5" style="max-height: 70vh; overflow-y: auto;">
          <v-form ref="incidentFormRef">
            <!-- Status Display (Read-only, always shown) -->
            <v-card rounded="xl" class="mb-4 pa-4" elevation="0" style="background: linear-gradient(135deg, #F3F6FF 0%, #FFFFFF 100%); border: 2px solid #E2E8F0;">
              <div class="text-center">
                <div class="text-caption font-weight-bold mb-3" style="color: #718096; text-transform: uppercase; letter-spacing: 0.05em;">
                  {{ $t('incidents.incident-status') }}
                </div>
                <v-chip
                  :color="getStatusColor(incidentForm.state)"
                  size="x-large"
                  variant="flat"
                  label
                  class="text-capitalize font-weight-bold px-6 py-3"
                  style="color: white;"
                >
                  <v-icon start size="22">{{ getStatusIcon(incidentForm.state) }}</v-icon>
                  {{ getStatusLabel(incidentForm.state) }}
                </v-chip>
                <p v-if="isEditMode && incidentForm.state === 'done'" class="text-caption mt-3 font-weight-medium" style="color: #DC3545;">
                  ⚠️ Incident is closed. You can only delete it.
                </p>
                <p v-else-if="isEditMode" class="text-caption mt-3" style="color: #9AA6B1;">
                  {{ $t('incidents.status-managed-by-admin') }}
                </p>
              </div>
            </v-card>

            <!-- Location Field -->
            <v-text-field
              v-model="incidentForm.location.locationName"
              :label="$t('incidents.incident-location')"
              variant="outlined"
              density="comfortable"
              rounded="lg"
              readonly
              prepend-inner-icon="mdi-map-marker"
              :append-inner-icon="isEditMode ? '' : 'mdi-crosshairs-gps'"
              color="#23A5E6"
              class="mb-4"
              :rules="[v => !!v || $t('incidents.location-required')]"
              @click="!isEditMode && openMapView()"
              @click:append-inner="!isEditMode && openMapView()"
            />
            <p v-if="isEditMode" class="text-caption mb-4" style="color: #9AA6B1; margin-top: -12px;">
              {{ $t('incidents.location-cannot-change') }}
            </p>

            <!-- Category Select -->
            <v-select
              v-model="incidentForm.serviceCategoryName"
              :items="availableCategories"
              :label="$t('incidents.incident-category')"
              variant="outlined"
              density="comfortable"
              rounded="lg"
              color="#23A5E6"
              class="mb-4"
              :readonly="isEditMode"
              :rules="[v => !!v || $t('incidents.category-required')]"
            >
              <template #prepend-inner>
                <v-icon size="20" color="#285096">{{ getCategoryIcon(incidentForm.serviceCategoryName) }}</v-icon>
              </template>
              <template #item="{ props, item }">
                <v-list-item v-bind="props">
                  <template #prepend>
                    <v-icon size="20" color="#285096">{{ getCategoryIcon(item.value) }}</v-icon>
                  </template>
                </v-list-item>
              </template>
            </v-select>
            <p v-if="isEditMode" class="text-caption mb-4" style="color: #9AA6B1; margin-top: -12px;">
              {{ $t('incidents.category-cannot-change') }}
            </p>

            <!-- Description Textarea -->
            <v-textarea
              v-model="incidentForm.description"
              :label="$t('incidents.description')"
              variant="outlined"
              density="comfortable"
              rounded="lg"
              prepend-inner-icon="mdi-text"
              color="#23A5E6"
              rows="4"
              auto-grow
              class="mb-4"
              :readonly="isEditMode && incidentForm.state === 'done'"
              :rules="[v => !!v || $t('incidents.description-required')]"
            />
            <p v-if="isEditMode && incidentForm.state === 'done'" class="text-caption mb-4" style="color: #DC3545; margin-top: -12px;">
              {{ $t('incidents.incident-resolved-message') }}
            </p>
            <p v-else-if="isEditMode && incidentForm.state !== 'done'" class="text-caption mb-4" style="color: #23A5E6; margin-top: -12px;">
              {{ $t('incidents.can-edit-description-images') }}
            </p>

            <!-- Image Upload (Premium Feature - Disabled) -->
            <div class="mb-4">
              <div class="d-flex align-center mb-2">
                <v-chip
                  color="#DC3545"
                  size="x-small"
                  variant="flat"
                  prepend-icon="mdi-star"
                  class="text-uppercase font-weight-bold mr-2"
                  style="letter-spacing: 0.5px;"
                >
                 {{ $t('incidents.premium-feature') }}
                </v-chip>
                <span class="text-body-2 font-weight-medium" style="color: #4E5965;">
                  {{ $t('incidents.upload-image') }}
                </span>
              </div>

              <v-file-input
                v-model="incidentForm.newImages"
                :label="$t('incidents.upload-image')"
                variant="outlined"
                density="comfortable"
                rounded="lg"
                prepend-inner-icon="mdi-camera"
                multiple
                accept="image/png, image/jpeg"
                show-size
                counter
                color="#9AA6B1"
                disabled
                readonly
                class="premium-disabled-input"
              >
                <template #prepend-inner>
                  <v-icon color="#9AA6B1">mdi-camera-off</v-icon>
                </template>
              </v-file-input>

              <p class="text-caption mt-2" style="color: #9AA6B1;">
                <v-icon size="14" color="#9AA6B1" class="mr-1">mdi-information-outline</v-icon>
                {{ $t('incidents.image-upload-premium') }}
              </p>
            </div>
          </v-form>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-5" style="background: #F8FAFC; border-top: 1px solid #E2E8F0;">
          <v-btn
            variant="text"
            color="#4E5965"
            rounded="lg"
            class="text-none"
            @click="closeDialog"
          >
            {{ $t('incidents.cancel') }}
          </v-btn>

          <v-spacer />

          <v-btn
            v-if="isEditMode"
            variant="tonal"
            color="#DC3545"
            rounded="lg"
            class="text-none mr-2"
            :loading="isDeleting"
            @click="confirmDeleteIncident"
          >
            <v-icon start size="18">mdi-delete</v-icon>
            {{ $t('incidents.delete') }}
          </v-btn>

          <v-btn
            variant="flat"
            color="#285096"
            rounded="lg"
            class="text-none px-6"
            :loading="isSaving"
            :disabled="isEditMode && incidentForm.state === 'done'"
            @click="saveIncident"
          >
            <v-icon start size="18">{{ isEditMode ? 'mdi-content-save' : 'mdi-content-save' }}</v-icon>
            {{ isEditMode ? $t('incidents.save-changes') || 'Update' : $t('incidents.create') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="420" persistent>
      <v-card rounded="xl" class="pa-5 text-center">
        <v-avatar color="#DC3545" size="72" variant="tonal" class="mb-4">
          <v-icon size="36" color="#DC3545">mdi-delete-alert</v-icon>
        </v-avatar>
        <h3 class="text-h6 font-weight-semibold mb-2" style="color: #001437;">
          {{ $t('incidents.delete-incident') }}
        </h3>
        <p class="text-body-2 mb-5" style="color: #4E5965; line-height: 1.6;">
          {{ $t('incidents.delete-incident-message') }}
        </p>
        <div class="d-flex justify-center ga-3">
          <v-btn
            variant="outlined"
            color="#4E5965"
            rounded="lg"
            min-width="120"
            class="text-none"
            @click="deleteDialog = false"
          >
            {{ $t('incidents.cancel') }}
          </v-btn>
          <v-btn
            variant="flat"
            color="#DC3545"
            rounded="lg"
            min-width="120"
            class="text-none"
            :loading="isDeleting"
            @click="deleteIncident"
          >
            {{ $t('incidents.delete') }}
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Map View Component -->
    <MapView ref="mapViewRef" :dragMarker="true" @onPositionSelected="handleLocationSelected" />

    <!-- Snackbar Notification -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="4000"
      rounded="lg"
      location="bottom right"
    >
      <div class="d-flex align-center ga-2 font-weight-medium">
        <v-icon size="20">{{ snackbar.icon }}</v-icon>
        {{ snackbar.message }}
      </div>
      <template #actions>
        <v-btn variant="text" class="text-none" @click="snackbar.show = false">
          {{ $t('booking.close') }}
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import moment from 'moment'
import MapView from '@/components/general/IncidentsMapView.vue'
import Loader from '@/components/general/Loader.vue'
import { getStoredItem } from '@/services/utils.js'
import { useOAuthService } from '@/services/OAuthService'
import {
  getIncidentsByUser,
  getIncidentById,
  createIncident,
  updateIncidentById,
  deleteIncidentById,
  getBuildings,
  getFloors,
  getIncidentCategories,
  getPoisByCategoryId,
  getPoiCategories
} from '@/controllers/incidentsAxiosBypasser'
import { isStaingOrQa, postMultipleFiles, linkIncidentToImage } from '@/controllers/BaseController'

export default {
  name: 'UserIncidents',
  components: { MapView, Loader },
  setup() {
    const router = useRouter()
    const { t } = useI18n()
    const oAuthService = useOAuthService()

    const mapViewRef = ref(null)
    const incidentFormRef = ref(null)

    const isLoading = ref(true)
    const isSaving = ref(false)
    const isDeleting = ref(false)
    const isRefreshing = ref(false)
    const isOpeningIncident = ref(false)
    const isOpeningMap = ref(false)
    const showLoader = computed(() => 
      isLoading.value || 
      isSaving.value || 
      isDeleting.value || 
      isRefreshing.value || 
      isOpeningIncident.value || 
      isOpeningMap.value
    )
    
    const searchQuery = ref('')
    const showFilters = ref(false)
    const incidentDialog = ref(false)
    const deleteDialog = ref(false)

    const userEmail = ref('')
    const userName = ref('')
    const incidents = ref([])
    const availableCategories = ref([])
    const floors = ref([])
    const selectedBuilding = ref(null)
    const buildingName = ref('')

    const snackbar = reactive({
      show: false,
      message: '',
      color: 'success',
      icon: 'mdi-check-circle'
    })

    const filters = reactive({
      timeRange: 'all',
      category: 'all',
      floor: 'all',
      status: 'all'
    })

    const currentSort = ref('newest')

    const incidentForm = reactive({
      id: null,
      version: null,
      location: {
        locationName: '',
        floorId: null,
        latitude: null,
        longitude: null
      },
      serviceCategoryName: '',
      description: '',
      state: 'open',
      newImages: []
    })

    const categoryIconMap = {
      'Sonstiges - (Other)': 'mdi-dots-horizontal-circle',
      'Sicherheit - (Security)': 'mdi-shield-lock',
      'Gesundheit und Sicherheit - (Health and Safety)': 'mdi-heart-pulse',
      'Wartung - (Maintenance)': 'mdi-tools',
      'IT-Probleme - (IT Problem)': 'mdi-monitor-cellphone',
      'Elektrisch - (Electrical)': 'mdi-lightning-bolt',
      'Reinigung - (Cleaning)': 'mdi-broom',
      'Umwelt - (Environmental)': 'mdi-leaf'
    }

    const getCategoryIcon = (categoryName) => {
      if (!categoryName) return 'mdi-tag-outline'
      return categoryIconMap[categoryName] || 'mdi-tag-outline'
    }

    const timeRangeOptions = computed(() => [
      { label: t('booking.all'), value: 'all' },
      { label: t('booking.today'), value: 'today' },
      { label: 'Past', value: 'past' },
      { label: t('booking.current-week'), value: 'week' },
      { label: t('booking.current-month'), value: 'month' }
    ])

    const categoryOptions = computed(() => {
      const categories = availableCategories.value.map(cat => ({ label: cat, value: cat }))
      return [{ label: t('occupancy.all-categories'), value: 'all' }, ...categories]
    })

    const floorOptions = computed(() => {
      const floorList = floors.value.map(f => ({ label: `Floor ${f.number}`, value: f.id }))
      return [{ label: t('occupancy.all-floors'), value: 'all' }, ...floorList]
    })

    const sortOptions = computed(() => [
      { label: t('navigation-bar.sort') + ' (Newest)', value: 'newest' },
      { label: t('navigation-bar.sort') + ' (Oldest)', value: 'oldest' },
      { label: t('incidents.incident-status'), value: 'status' },
      { label: t('incidents.incident-category'), value: 'category' },
      { label: t('office.location'), value: 'location' }
    ])

    const currentSortLabel = computed(() => {
      const option = sortOptions.value.find(o => o.value === currentSort.value)
      return option?.label || t('navigation-bar.sort')
    })

    const hasActiveFilters = computed(() =>
      filters.timeRange !== 'all' ||
      filters.category !== 'all' ||
      filters.floor !== 'all' ||
      filters.status !== 'all'
    )

    const activeFilterCount = computed(() => {
      let count = 0
      if (filters.timeRange !== 'all') count++
      if (filters.category !== 'all') count++
      if (filters.floor !== 'all') count++
      if (filters.status !== 'all') count++
      return count
    })

    const isEditMode = computed(() => !!incidentForm.id)
    
    const isIncidentClosed = computed(() => incidentForm.state === 'done')
    
    const canEditIncident = computed(() => {
      if (!isEditMode.value) return true // Create mode
      return incidentForm.state !== 'done' // Edit mode: only if not done
    })

    const tableHeaders = computed(() => [
      { title: 'ID', key: 'incidentNumber', width: '110' },
      { title: t('incidents.description'), key: 'description', minWidth: '180' },
      { title: t('office.location'), key: 'location', minWidth: '150' },
      { title: t('office.floor'), key: 'floorName', width: '80' },
      { title: t('incidents.incident-category'), key: 'serviceCategoryName', minWidth: '160' },
      { title: t('incidents.incident-date'), key: 'creationTime', width: '120' },
      { title: t('incidents.incident-status'), key: 'state', width: '120', align: 'end' }
    ])

    const getFloorIdFromIncident = (incident) => {
      if (incident.location?.floorId) return incident.location.floorId

      if (incident.location?.locationName) {
        const parts = incident.location.locationName.split(': ')
        if (parts.length > 1) {
          const floorName = parts[1].trim().toLowerCase()
          const floor = floors.value.find(f => f.name.toLowerCase() === floorName)
          if (floor) return floor.id
        }
      }

      return null
    }

    const getFloorDisplay = (incident) => {
      const floorId = getFloorIdFromIncident(incident)
      if (!floorId) return '-'
      const floor = floors.value.find(f => f.id === floorId)
      return floor ? floor.number : '-'
    }

    const getLocationDisplay = (incident) => {
      return incident.location?.locationName?.split(',')[0] || 'Unknown'
    }

    const filteredIncidents = computed(() => {
      let result = [...incidents.value]

      if (searchQuery.value?.trim()) {
        const query = searchQuery.value.toLowerCase()
        result = result.filter(inc => {
          const number = inc.serviceNumber || inc.incidentNumber || ''
          return (
            inc.description?.toLowerCase().includes(query) ||
            getLocationDisplay(inc).toLowerCase().includes(query) ||
            (inc.serviceCategoryName || inc.incidentCategoryName)?.toLowerCase().includes(query) ||
            String(number).toLowerCase().includes(query) ||
            inc.state?.toLowerCase().includes(query) ||
            String(getFloorDisplay(inc)).toLowerCase().includes(query)
          )
        })
      }

      if (filters.timeRange !== 'all') {
        const now = moment()
        const today = moment().startOf('day')
        result = result.filter(inc => {
          const date = moment(inc.creationTime)
          switch (filters.timeRange) {
            case 'past': return date.isBefore(today)
            case 'today': return date.isSame(now, 'day')
            case 'week': return date.isSame(now, 'week')
            case 'month': return date.isSame(now, 'month')
            default: return true
          }
        })
      }

      if (filters.category !== 'all') {
        result = result.filter(inc =>
          (inc.serviceCategoryName || inc.incidentCategoryName) === filters.category
        )
      }

      if (filters.floor !== 'all') {
        result = result.filter(inc => {
          const floorId = getFloorIdFromIncident(inc)
          return floorId === filters.floor
        })
      }

      if (filters.status !== 'all') {
        result = result.filter(inc => {
          // Treat pending as open in filter (pending is hidden from users)
          if (filters.status === 'open') {
            return inc.state === 'open' || inc.state === 'pending'
          }
          return inc.state === filters.status
        })
      }

      result.sort((a, b) => {
        switch (currentSort.value) {
          case 'newest': return new Date(b.creationTime) - new Date(a.creationTime)
          case 'oldest': return new Date(a.creationTime) - new Date(b.creationTime)
          case 'status': {
            const order = { open: 0, done: 1 }
            return (order[a.state] || 0) - (order[b.state] || 0)
          }
          case 'category':
            return ((a.serviceCategoryName || a.incidentCategoryName) || '').localeCompare((b.serviceCategoryName || b.incidentCategoryName) || '')
          case 'location':
            return getLocationDisplay(a).localeCompare(getLocationDisplay(b))
          default: return 0
        }
      })

      return result
    })

    const statusSummary = computed(() => {
      const summary = { open: 0, done: 0 }
      incidents.value.forEach(i => {
        // Treat pending as open in the UI (users don't see pending state)
        if (i.state === 'open' || i.state === 'pending') {
          summary.open++
        } else if (i.state === 'done') {
          summary.done++
        }
      })
      return summary
    })

    const showNotification = (message, type = 'success') => {
      snackbar.message = message
      snackbar.color = type === 'success' ? '#28A745' : type === 'error' ? '#DC3545' : '#23A5E6'
      snackbar.icon = type === 'success' ? 'mdi-check-circle' : type === 'error' ? 'mdi-alert-circle' : 'mdi-information'
      snackbar.show = true
    }

    const logError = (context, err) => {
      console.error(`[UserIncidents] ${context}:`, err?.message || err)
    }

    const formatDateTime = dateStr => !dateStr ? '' : moment(dateStr).format('DD MMM, HH:mm')
    const truncateText = (text, maxLen) => !text ? '' : text.length > maxLen ? text.substring(0, maxLen) + '...' : text

    const getStatusColor = status => {
      // Treat pending as open in UI (users don't see pending state)
      if (status === 'open' || status === 'pending') return '#DC3545'
      if (status === 'done') return '#28A745'
      return '#9AA6B1'
    }

    const getStatusLabel = status => {
      // Treat pending as open in UI
      if (status === 'open' || status === 'pending') return t('incidents.incident-open')
      if (status === 'done') return t('office.done')
      return status
    }

    const getStatusIcon = status => {
      // Treat pending as open in UI
      if (status === 'open' || status === 'pending') return 'mdi-alert-circle'
      if (status === 'done') return 'mdi-check-circle'
      return 'mdi-help-circle'
    }

    const clearFilters = () => {
      filters.timeRange = 'all'
      filters.category = 'all'
      filters.floor = 'all'
      filters.status = 'all'
    }

    const loadIncidents = async () => {
      try {
        const profile = await oAuthService.getUserProfile()
        userEmail.value = profile.email
        userName.value = profile.name || profile.email.split('@')[0]

        const userIncidents = await getIncidentsByUser(profile.email)

        userIncidents.forEach((incident) => {
          // Location comes directly from API location object (not tags)
          if (!incident.location || !incident.location.locationName) {
            incident.location = {
              locationName: 'Unknown',
              floorId: null,
              latitude: null,
              longitude: null
            }
          }
          
          // Use state from API directly (not from tags)
          if (!incident.state) {
            incident.state = 'open'
          }
          
          // Ensure category name is available
          if (!incident.serviceCategoryName && incident.incidentCategoryName) {
            incident.serviceCategoryName = incident.incidentCategoryName
          }
        })

        incidents.value = userIncidents
      } catch (err) {
        logError('loadIncidents', err)
        showNotification(t('incidents.loading-error'), 'error')
      }
    }

    const initializeComponent = async () => {
      isLoading.value = true
      try {
        const [buildings, isPIA, categories, floorList] = await Promise.all([
          getBuildings(),
          isStaingOrQa(),
          getIncidentCategories(),
          getFloors()
        ])

        buildingName.value = isPIA
          ? buildings.find(b => b.name === 'Teichhaus')?.name
          : getStoredItem('building')

        selectedBuilding.value = buildings.find(b => b.name === buildingName.value)

        availableCategories.value = categories
          .filter(cat => cat?.campusId === selectedBuilding.value?.campus?.id)
          .map(cat => cat.name)

        floors.value = floorList.filter(floor =>
          floor?.pois?.ids?.length > 0 &&
          floor?.building?.id === selectedBuilding.value?.id
        )

        await loadIncidents()
      } catch (err) {
        logError('initializeComponent', err)
        showNotification(t('incidents.loading-error'), 'error')
      } finally {
        isLoading.value = false
      }
    }

    const handleRefresh = async () => {
      isRefreshing.value = true
      try {
        await loadIncidents()
        showNotification(t('incidents.refreshing'))
      } catch (err) {
        logError('handleRefresh', err)
        showNotification(t('incidents.refresh-failed'), 'error')
      } finally {
        isRefreshing.value = false
      }
    }

    const resetForm = () => {
      incidentForm.id = null
      incidentForm.version = null
      incidentForm.location = { locationName: '', floorId: null, latitude: null, longitude: null }
      incidentForm.serviceCategoryName = ''
      incidentForm.description = ''
      incidentForm.state = 'open'
      incidentForm.newImages = []
    }

    const openCreateDialog = () => {
      resetForm()
      incidentDialog.value = true
    }

    const openIncidentDialog = async (incident) => {
      isOpeningIncident.value = true
      try {
        const inc = await getIncidentById(incident.id)

        incidentForm.id = inc.id
        incidentForm.version = inc.version
        incidentForm.location = {
          locationName: inc.location?.locationName || '',
          floorId: inc.location?.floorId || null,
          latitude: inc.location?.latitude || null,
          longitude: inc.location?.longitude || null
        }
        incidentForm.serviceCategoryName = inc.incidentCategoryName || inc.serviceCategoryName || ''
        incidentForm.description = inc.description || ''
        incidentForm.state = inc.state || 'open'
        incidentForm.newImages = []

        incidentDialog.value = true
      } catch (err) {
        logError('openIncidentDialog', err)
        showNotification(t('incidents.loading-error'), 'error')
      } finally {
        isOpeningIncident.value = false
      }
    }

    const closeDialog = () => {
      incidentDialog.value = false
      resetForm()
    }

    const openMapView = async () => {
      isOpeningMap.value = true
      try {
        const poiCategories = await getPoiCategories()
        let allPois = []
        for (const cat of poiCategories) {
          try {
            const pois = await getPoisByCategoryId(cat.id)
            allPois = allPois.concat(pois)
          } catch (err) {
            logError('loadPois', err)
          }
        }

        if (mapViewRef.value) {
          if (incidentForm.location.latitude && incidentForm.location.longitude) {
            mapViewRef.value.openMap(
              selectedBuilding.value,
              allPois,
              poiCategories,
              {
                lat: incidentForm.location.latitude,
                long: incidentForm.location.longitude
              }
            )
          } else {
            mapViewRef.value.openMap(selectedBuilding.value, allPois, poiCategories)
          }
        }
      } catch (err) {
        logError('openMapView', err)
        showNotification(t('incidents.error-incident'), 'error')
      } finally {
        isOpeningMap.value = false
      }
    }

    const handleLocationSelected = ({ coordinates, floor, locationName }) => {
      if (!coordinates) return
      incidentForm.location.latitude = coordinates[0]
      incidentForm.location.longitude = coordinates[1]
      incidentForm.location.locationName = locationName || ''
      incidentForm.location.floorId = floor?.id || null
    }

    const getImagePreview = file => file instanceof File ? URL.createObjectURL(file) : ''
    const removeNewImage = index => incidentForm.newImages.splice(index, 1)

    const resizeImage = (file, maxW = 750, maxH = 498) => {
      return new Promise((resolve, reject) => {
        const img = new Image()
        img.src = URL.createObjectURL(file)
        img.onload = () => {
          const canvas = document.createElement('canvas')
          let w = img.width, h = img.height
          if (w > maxW) { h *= maxW / w; w = maxW }
          if (h > maxH) { w *= maxH / h; h = maxH }
          canvas.width = w
          canvas.height = h
          canvas.getContext('2d').drawImage(img, 0, 0, w, h)
          canvas.toBlob(blob => {
            URL.revokeObjectURL(img.src)
            blob ? resolve(new File([blob], file.name, { type: file.type })) : reject(new Error('Resize failed'))
          }, 'image/png')
        }
        img.onerror = () => {
          URL.revokeObjectURL(img.src)
          reject(new Error('Load failed'))
        }
      })
    }

    const confirmDeleteIncident = () => {
      deleteDialog.value = true
    }

    const deleteIncident = async () => {
      if (!incidentForm.id) return
      isDeleting.value = true
      try {
        await deleteIncidentById(incidentForm.id)
        deleteDialog.value = false
        incidentDialog.value = false
        showNotification(t('incidents.deleted-incident'))
        await loadIncidents()
      } catch (err) {
        logError('deleteIncident', err)
        showNotification(t('incidents.delete-incident-failed'), 'error')
      } finally {
        isDeleting.value = false
      }
    }

    const saveIncident = async () => {
      const { valid } = await incidentFormRef.value.validate()
      if (!valid) return

      isSaving.value = true

      try {
        if (isEditMode.value) {
          if (incidentForm.state === 'done') {
            showNotification('Incident is closed. You can only delete it.', 'info')
            return
          }

          console.log('User editing incident:', incidentForm.id)
          
          const latest = await getIncidentById(incidentForm.id)
          
          const updatePayload = {
            id: latest.id,
            version: latest.version,
            tags: latest.tags || {},
            files: latest.files || { type: 'files', entities: [] },
            building: latest.building,
            campus: latest.campus,
            incidentCategory: latest.incidentCategory,
            location: latest.location,
            creationTime: latest.creationTime,
            description: incidentForm.description,
            state: latest.state,
            creatorName: latest.creatorName || '',
            creatorEmail: latest.creatorEmail || '',
            incidentNumber: latest.incidentNumber,
            serviceOrder: latest.serviceOrder,
            incidentCategoryName: latest.incidentCategoryName,
            buildingName: latest.buildingName,
            floorName: latest.floorName,
            type: 'incidents'
          }

          await updateIncidentById(incidentForm.id, updatePayload)
          console.log('✓ Description updated')

          showNotification(t('incidents.updated-incident') || 'Incident updated successfully')
        } else {
          console.log('Creating new incident')
          
          const categories = await getIncidentCategories()
          const selectedCategory = categories.find(cat => cat.name === incidentForm.serviceCategoryName)
          
          if (!selectedCategory) {
            throw new Error('Category not found')
          }

          const payload = {
            building: {
              type: 'buildings',
              id: selectedBuilding.value?.id
            },
            buildingId: selectedBuilding.value?.id,
            incidentCategoryId: selectedCategory.id,
            campus: {
              type: 'campuses',
              id: selectedBuilding.value?.campus?.id
            },
            incidentCategory: {
              type: 'incidentcategories',
              id: selectedCategory.id
            },
            location: {
              locationName: incidentForm.location.locationName,
              floorId: incidentForm.location.floorId,
              latitude: incidentForm.location.latitude,
              longitude: incidentForm.location.longitude
            },
            description: incidentForm.description,
            state: 'open',
            incidentCategoryName: incidentForm.serviceCategoryName,
            type: 'incidents'
          }

          console.log('Creating incident with payload:', payload)
          const created = await createIncident(payload)

          if (!created?.id) {
            throw new Error('Failed to create incident')
          }

          console.log('Incident created:', created.id)

          showNotification(t('incidents.created-incident') || 'Incident created successfully')
        }

        incidentDialog.value = false
        await loadIncidents()
      } catch (err) {
        logError('saveIncident', err)
        const errorMsg = err.message || 'Unknown error'
        showNotification(`${t('incidents.failed-incident') || 'Failed to save incident'}: ${errorMsg}`, 'error')
      } finally {
        isSaving.value = false
      }
    }

    onMounted(async () => {
      await initializeComponent()
    })

    return {
      mapViewRef,
      incidentFormRef,
      isLoading,
      isSaving,
      isDeleting,
      isRefreshing,
      isOpeningIncident,
      isOpeningMap,
      showLoader,
      searchQuery,
      showFilters,
      incidentDialog,
      deleteDialog,
      userName,
      incidents,
      availableCategories,
      floors,
      snackbar,
      filters,
      currentSort,
      incidentForm,
      timeRangeOptions,
      categoryOptions,
      floorOptions,
      sortOptions,
      currentSortLabel,
      hasActiveFilters,
      activeFilterCount,
      isEditMode,
      isIncidentClosed,
      canEditIncident,
      tableHeaders,
      filteredIncidents,
      statusSummary,
      showNotification,
      formatDateTime,
      truncateText,
      getStatusColor,
      getStatusLabel,
      getStatusIcon,
      clearFilters,
      handleRefresh,
      openCreateDialog,
      openIncidentDialog,
      closeDialog,
      openMapView,
      handleLocationSelected,
      getImagePreview,
      removeNewImage,
      confirmDeleteIncident,
      deleteIncident,
      saveIncident,
      getFloorDisplay,
      getLocationDisplay,
      getCategoryIcon
    }
  }
}
</script>

<style scoped>
.responsive-table :deep(.v-data-table__td) {
  white-space: normal !important;
  vertical-align: top !important;
  padding-top: 12px !important;
  padding-bottom: 12px !important;
}

.responsive-table :deep(.v-data-table__th) {
  white-space: nowrap !important;
}

.responsive-table :deep(table) {
  table-layout: auto !important;
}

/* Mobile Card Styles */
.incident-card {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: linear-gradient(135deg, #ffffff 0%, #fafbfc 100%);
}

.incident-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(40, 80, 150, 0.12) !important;
  border-color: rgba(40, 80, 150, 0.2);
}

.incident-card:active {
  transform: translateY(0px);
  transition: all 0.1s;
}

.detail-item {
  display: flex;
  align-items: center;
  min-height: 24px;
}

/* Status chip styling */
.incident-card :deep(.v-chip) {
  font-size: 0.65rem;
  height: 20px;
  padding: 0 8px;
}

/* Smooth transitions for all interactive elements */
.incident-card * {
  transition: color 0.2s ease;
}

/* Premium Disabled Input Styling */
.premium-disabled-input :deep(.v-input__control) {
  opacity: 0.6;
  cursor: not-allowed;
  pointer-events: none;
}

.premium-disabled-input :deep(.v-field) {
  background-color: #F7FAFC !important;
  border-color: #E2E8F0 !important;
}

.premium-disabled-input :deep(.v-field__input) {
  color: #9AA6B1 !important;
}

.premium-disabled-input :deep(.v-icon) {
  opacity: 0.5;
}
</style>
